/**
 * PATH: apps/hr/src/pages/employees/EmployeeDetailPage.tsx
 * PURPOSE: Employee detail page orchestrator — tabs, modals, sidebar
 */

import { ArrowLeft, MoreVertical, Users, Ban, Trash2, Loader2, User, FileText, Ticket } from 'lucide-react'
import { cn, getInitials, formatStatus } from '../../lib/utils'
import type { EmployeeStatus } from '../../types'
import { useEmployeeDetail } from './employee-detail/useEmployeeDetail'
import { type Tab, TabNavigation } from './employee-detail/detail-shared-ui'
import { EditEmploymentModal, EditContactModal, EditEmergencyContactModal, EditBankDetailsModal, EditPersonalInfoModal } from './employee-detail/EditModals'
import OverviewTab from './employee-detail/OverviewTab'
import DocumentsTab from './employee-detail/DocumentsTab'
import SupportTab from './employee-detail/SupportTab'
import EmployeeSidebar from './employee-detail/EmployeeSidebar'

export default function EmployeeDetailPage() {
  const ctx = useEmployeeDetail()
  const {
    navigate, employee, apiLoading, apiError, refetch, documents, refetchDocuments,
    activeTab, setActiveTab,
    editEmploymentOpen, setEditEmploymentOpen, editContactOpen, setEditContactOpen,
    editEmergencyOpen, setEditEmergencyOpen, editBankOpen, setEditBankOpen,
    editPersonalOpen, setEditPersonalOpen, actionsMenuOpen, setActionsMenuOpen,
    confirmBlockOpen, setConfirmBlockOpen, confirmDeleteOpen, setConfirmDeleteOpen,
    isSaving, handleSaveEmployment, handleSaveContact, handleSaveEmergency,
    handleSaveBank, handleSavePersonal, handleBlockUser, handleDeleteEmployee,
  } = ctx

  // Loading
  if (apiLoading) {
    return <div className="flex flex-col items-center justify-center h-[60vh]"><Loader2 className="w-12 h-12 text-teal-600 animate-spin mb-4" /><p className="text-slate-500 dark:text-slate-400">Loading employee...</p></div>
  }

  // Not found
  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <Users className="w-16 h-16 text-slate-300 dark:text-slate-700 mb-4" />
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">Employee Not Found</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-4">{apiError ? 'Could not load employee. Please try again.' : "The employee doesn't exist."}</p>
        <div className="flex gap-3">
          {apiError && <button onClick={() => refetch()} className="px-4 py-2 text-sm font-medium text-teal-600 border border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg">Retry</button>}
          <button onClick={() => navigate('/employees')} className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg">Back to Employees</button>
        </div>
      </div>
    )
  }

  const statusColors: Record<EmployeeStatus, string> = {
    active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    on_leave: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    inactive: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300',
    terminated: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  }

  const tabs: Tab[] = [
    { id: 'overview', label: 'Overview', icon: User },
    { id: 'documents', label: 'Documents', icon: FileText, count: documents.length },
    { id: 'support', label: 'Support', icon: Ticket },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/employees')} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition-colors"><ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" /></button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-semibold">{getInitials(`${employee.firstName} ${employee.lastName}`)}</div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold text-slate-900 dark:text-white">{employee.firstName} {employee.lastName}</h1>
                <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', statusColors[employee.status])}>{formatStatus(employee.status)}</span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">{employee.position} • {employee.department}</p>
            </div>
          </div>
        </div>
        {/* Actions Menu */}
        <div className="relative">
          <button onClick={() => setActionsMenuOpen(!actionsMenuOpen)} className="p-2 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800"><MoreVertical className="w-4 h-4" /></button>
          {actionsMenuOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setActionsMenuOpen(false)} />
              <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1 z-20">
                <button onClick={() => { setActionsMenuOpen(false); setConfirmBlockOpen(true) }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"><Ban className="w-4 h-4 text-amber-500" />{employee.status === 'inactive' ? 'Unblock User' : 'Block User'}</button>
                <button onClick={() => { setActionsMenuOpen(false); setConfirmDeleteOpen(true) }} className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"><Trash2 className="w-4 h-4" />Delete Employee</button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 lg:grid-cols-[1fr,320px]">
        <div className="space-y-6">
          <TabNavigation tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
          {activeTab === 'overview' && <OverviewTab employee={employee} onEditEmployment={() => setEditEmploymentOpen(true)} onEditContact={() => setEditContactOpen(true)} onEditEmergency={() => setEditEmergencyOpen(true)} onEditBank={() => setEditBankOpen(true)} onEditPersonal={() => setEditPersonalOpen(true)} />}
          {activeTab === 'documents' && <DocumentsTab documents={documents} employeeId={employee.id} onRefresh={refetchDocuments} />}
          {activeTab === 'support' && <SupportTab employeeId={employee.id} employeeName={`${employee.firstName} ${employee.lastName}`} />}
        </div>
        <EmployeeSidebar employee={employee} />
      </div>

      {/* Edit Modals */}
      <EditEmploymentModal isOpen={editEmploymentOpen} onClose={() => setEditEmploymentOpen(false)} employee={employee} onSave={handleSaveEmployment} isSaving={isSaving} />
      <EditContactModal isOpen={editContactOpen} onClose={() => setEditContactOpen(false)} employee={employee} onSave={handleSaveContact} isSaving={isSaving} />
      <EditEmergencyContactModal isOpen={editEmergencyOpen} onClose={() => setEditEmergencyOpen(false)} employee={employee} onSave={handleSaveEmergency} isSaving={isSaving} />
      <EditBankDetailsModal isOpen={editBankOpen} onClose={() => setEditBankOpen(false)} employee={employee} onSave={handleSaveBank} isSaving={isSaving} />
      <EditPersonalInfoModal isOpen={editPersonalOpen} onClose={() => setEditPersonalOpen(false)} employee={employee} onSave={handleSavePersonal} isSaving={isSaving} />

      {/* Block Confirmation */}
      {confirmBlockOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setConfirmBlockOpen(false)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center"><Ban className="w-5 h-5 text-amber-600 dark:text-amber-400" /></div>
              <div><h3 className="text-lg font-semibold text-slate-900 dark:text-white">{employee.status === 'inactive' ? 'Unblock User' : 'Block User'}</h3><p className="text-sm text-slate-500 dark:text-slate-400">{employee.firstName} {employee.lastName}</p></div>
            </div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">{employee.status === 'inactive' ? 'This will reactivate the user account.' : 'This will deactivate the user account.'}</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmBlockOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
              <button onClick={handleBlockUser} disabled={isSaving} className={cn('px-4 py-2 text-sm font-medium rounded-lg flex items-center gap-2', employee.status === 'inactive' ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'bg-amber-600 hover:bg-amber-700 text-white')}>
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}{employee.status === 'inactive' ? 'Unblock' : 'Block'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {confirmDeleteOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setConfirmDeleteOpen(false)} />
          <div className="relative bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md m-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center"><Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" /></div>
              <div><h3 className="text-lg font-semibold text-slate-900 dark:text-white">Delete Employee</h3><p className="text-sm text-slate-500 dark:text-slate-400">{employee.firstName} {employee.lastName}</p></div>
            </div>
            <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-4"><p className="text-sm text-red-700 dark:text-red-400"><strong>Warning:</strong> This cannot be undone. All data will be permanently deleted.</p></div>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">Are you sure you want to permanently delete this employee?</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setConfirmDeleteOpen(false)} className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg">Cancel</button>
              <button onClick={handleDeleteEmployee} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2 disabled:opacity-50">
                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}Delete Permanently
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
